import { useEffect, useState, type ReactNode } from 'react'

import { ApiError } from '../api/client.js'

/**
 * Tabel data dengan paginasi server-side — W1-B05.
 *
 * Komponen tidak pernah menerima seluruh data; ia meminta per halaman. Batasnya
 * ditegakkan server (maksimal 100 baris), dan komponen ini tidak menyediakan
 * cara memintanya lebih. Pelajaran 15.873 baris pada sistem lama berlaku di
 * kedua sisi.
 */

export interface Column<T> {
  readonly key: string
  readonly label: string
  readonly render: (row: T) => ReactNode
}

export interface PageResult<T> {
  readonly rows: readonly T[]
  readonly total: number
}

interface Props<T> {
  readonly columns: readonly Column<T>[]
  readonly fetchPage: (page: number, q: string) => Promise<PageResult<T>>
  /** Naikkan untuk memaksa muat ulang setelah mutasi. */
  readonly version?: number
  readonly emptyText?: string
}

const LIMIT = 25

export function DataTable<T>({ columns, fetchPage, version = 0, emptyText }: Props<T>): ReactNode {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [applied, setApplied] = useState('')
  const [data, setData] = useState<PageResult<T> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    setBusy(true)
    setError(null)
    fetchPage(page, applied)
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Tidak dapat menghubungi server')
        }
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [page, applied, version, fetchPage])

  const totalPages = data === null ? 1 : Math.max(1, Math.ceil(data.total / LIMIT))

  return (
    <div className="dt">
      <form
        className="dt-search"
        onSubmit={(e) => {
          e.preventDefault()
          setPage(1)
          setApplied(q.trim())
        }}
      >
        <input
          placeholder="Cari…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Kata pencarian"
        />
        <button type="submit" disabled={busy}>
          Cari
        </button>
      </form>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
          {data !== null && data.rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="dt-empty">
                {emptyText ?? 'Belum ada data'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="dt-pager">
        <button disabled={busy || page <= 1} onClick={() => setPage((p) => p - 1)}>
          ‹ Sebelumnya
        </button>
        <span>
          Halaman {page} dari {totalPages}
          {data !== null && ` — ${data.total} baris`}
        </span>
        <button disabled={busy || page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Berikutnya ›
        </button>
      </div>
    </div>
  )
}
